import { useState, useEffect } from "react";

interface TimeoutIndicatorProps {
    lastUpdateTime: string | { $date: string } | any;
    timeoutMinutes: number;
    currentStep: string;
}

export function TimeoutIndicator({ lastUpdateTime, timeoutMinutes, currentStep }: TimeoutIndicatorProps) {
    const [timeRemaining, setTimeRemaining] = useState<number>(0);
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        const calculateTimeRemaining = () => {
            // If no lastUpdateTime provided, don't show timeout (use current time as start)
            if (!lastUpdateTime) {
                const timeoutMs = timeoutMinutes * 60 * 1000;
                setTimeRemaining(timeoutMs);
                setIsExpired(false);
                return;
            }

            // Handle MongoDB date objects: {$date: timestamp} or just timestamp string
            let dateValue = lastUpdateTime;

            // Если это MongoDB объект {$date: ...}
            if (typeof lastUpdateTime === 'object' && lastUpdateTime.$date) {
                dateValue = lastUpdateTime.$date;
            }

            if (typeof dateValue === 'string') {
                // 1️⃣ Обрезаем микросекунды, оставляем 3 цифры
                dateValue = dateValue.replace(/\.(\d{3})\d*/, '.$1');
                // 2️⃣ Добавляем Z для UTC, если его нет
                if (!dateValue.endsWith('Z')) {
                    dateValue += 'Z';
                }
            }

            // создаём дату
            const lastUpdate = new Date(dateValue).getTime();
            const now = Date.now();
            
            // Check if date is valid
            if (isNaN(lastUpdate)) {
                console.error("Invalid lastUpdateTime:", lastUpdateTime, "parsed as:", dateValue);
                // For invalid dates, assume just started - don't mark as expired
                const timeoutMs = timeoutMinutes * 60 * 1000;
                setTimeRemaining(timeoutMs);
                setIsExpired(false);
                return;
            }
            
            const timeoutMs = timeoutMinutes * 60 * 1000;
            const elapsed = now - lastUpdate;
            const remaining = Math.max(0, timeoutMs - elapsed);

            setTimeRemaining(remaining);
            setIsExpired(remaining === 0);
        };

        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);

        return () => clearInterval(interval);
    }, [lastUpdateTime, timeoutMinutes]);

    const formatTime = (ms: number): string => {
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const getProgressPercentage = (): number => {
        const timeoutMs = timeoutMinutes * 60 * 1000;
        return Math.max(0, Math.min(100, (timeRemaining / timeoutMs) * 100));
    };

    const getStatusClass = (): string => {
        const percentage = getProgressPercentage();
        if (isExpired) return 'expired';
        if (percentage < 20) return 'critical';
        if (percentage < 50) return 'warning';
        return 'normal';
    };

    if (isExpired) {
        return (
            <div className={`timeout-indicator ${getStatusClass()}`}>
                <div className="timeout-icon">⏰</div>
                <div className="timeout-content">
                    <div className="timeout-label">Истекло время ожидания</div>
                    <div className="timeout-message">
                        Сделка будет автоматически отменена
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className={`timeout-indicator ${getStatusClass()}`}>
            <div className="timeout-icon">⏱️</div>
            <div className="timeout-content">
                <div className="timeout-label">Осталось времени</div>
                <div className="timeout-time">{formatTime(timeRemaining)}</div>
                <div className="timeout-progress">
                    <div 
                        className={`timeout-progress-bar progress-${Math.floor(getProgressPercentage() / 10) * 10}`}
                    />
                </div>
                <div className="timeout-hint">
                    {getProgressPercentage() < 20 && (
                        <span className="urgent">⚠️ Поторопитесь! Осталось мало времени</span>
                    )}
                    {getProgressPercentage() >= 20 && getProgressPercentage() < 50 && (
                        <span>Не забудьте выполнить действие вовремя</span>
                    )}
                </div>
            </div>
        </div>
    );
}
